import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { SitesService } from 'src/app/sites/sites.service';
import { DeviceFactory } from '../../../../device-factory/device-factory';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { SiteDashboardService } from 'src/app/home/site-dashboard/site-dashboard.service';

@Component({
	selector: 'app-daily-utilization-chart',
	templateUrl: './daily-utilization-chart.component.html',
	styleUrls: ['./daily-utilization-chart.component.css']
})
export class DailyUtilizationChartComponent implements OnInit, OnChanges, OnDestroy {
	@Input() chart = {firstChartTitle: "", secondChartTitle: "", dateFormate: "yyyy-MM-dd", firstChartYAxeTitle: ""};
	@Input() maxValue: number = 800;
	@Input() showUsageValue: boolean = false;
	@Input() devicesType = [DeviceFactory.types.charglink, DeviceFactory.types.brm];
	@Input() hasTruckTypeFilter = false;
	@Input() hasVoltageFilter = false;
	@Input() hasCapacityFilter = false;
	@Input() title: string = '';
	@Input() chartType: string = 'usage';
	@Input() tooltipText: string = '';
	@Output() prepareParentFiltersOptions = new EventEmitter<{
		batteryVoltagesOptions?: any[],
		batteryCapacitiesOptions?: any[],
		truckTypes?: any[],
		truckManufacturers?: any[],
	}>();

	truckTypes = [];
	@Input() selectedTruckType = "All";
	@Input() selectedTruckManufacturer = "All";
	noData: boolean = false;
	siteId: number = 0;
	devices: any = {};
	data: any = {};
	capacities: any = {};
	siteSubscription: any;

	showChart = false;
	@Input() toDate = new Date();
	@Input() fromDate = new Date(this.toDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	siteInstallationDate: Date = new Date(0);

	@Input() vAxisTitle: string;

	usedDevicesNumberChart: {data: any, columnNames: string[], config: any} = {
		data: [],
		columnNames: [
			this.translate.instant('g.date'), // date
			this.translate.instant('g.used'), // used
			this.translate.instant('g.not_used'), // not used
			this.translate.instant('g.total'), // total
		],
		config: {
			isStacked: true,
			hAxis: {
				title: this.translate.instant('g.date'),
				format: 'yyyy-MM-dd',
			},
			vAxis: {
				title: '',
				format: '0',
			},
			colors: ['#327339', '#b0c7b3', '#e2431e'],
			legend: {
				position: 'top',
				alignment: 'center',
				textStyle: { fontSize: 12 }
			},
			titleTextStyle: {
				fontSize: 16,
				bold: true,
				color: '#333',
				paddingBottom: 20,
				paddingTop: 20
			},
			annotations: {
				alwaysOutside: false,
				textStyle: {
					fontSize: 10,
					color: '#000',
					auraColor: 'none'
				}
			},
			series: {
				2: { type: 'line', targetAxisIndex: 0 }
			},
			tooltip: {
				isHtml: true
			}
		}
	};

	usagePercentageChart: {data: any, columnNames: string[], config: any} = {
		data: [],
		columnNames: [this.translate.instant('g.date'), this.translate.instant('g.usage_percentage')],
		config: {
			hAxis: {
				title: this.translate.instant('g.date'),
				format: 'yyyy-MM-dd',
			},
			vAxis: {
				title: this.translate.instant('g.usage_percentage'),
			},
			colors: ['#327339'],
			legend: {
				position: 'top',
				alignment: 'center',
				textStyle: { fontSize: 12 }
			},
			titleTextStyle: {
				fontSize: 16,
				bold: true,
				color: '#333',
				paddingBottom: 20,
				paddingTop: 20
			},
		}
	}

	@Input() batteryVoltagesOptions: any[] = [];
	@Input() selectedBatteryVoltage: string = 'all';
	@Input() batteryCapacitiesOptions: any[] = [];
	@Input() selectedBatteryCapacity: string = 'all';
	@Input() selectedBatteryVintage: string[] = ['all'];

	@Input() userParentFilters: boolean = false;

	constructor(
		private translate: TranslateService,
		private sitesService: SitesService,
		private route: ActivatedRoute,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private siteDashboardService: SiteDashboardService,
	) {}

	ngOnInit() {
		if (!this.vAxisTitle)
			this.vAxisTitle = this.translate.instant('site.number_of_devices');

		this.usedDevicesNumberChart.config.vAxis.title = this.vAxisTitle;

		this.siteId = this.route.parent.snapshot.params.siteId || 0;

		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo:any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				if (this.siteInstallationDate > this.fromDate)
					this.fromDate = this.siteInstallationDate;
			}

			if (this.chartType === 'ah') {
				this.getAhDailyUtilizationData();
				this.usedDevicesNumberChart.columnNames = [
					this.translate.instant('g.date'), // date
					this.translate.instant('g.used'), // used
					this.translate.instant('g.not_used'), // not used
					this.translate.instant('batteries_customized_charts.available_ah'), // total
				];
			} else {
				this.getDailyUtilizationData();
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (this.userParentFilters) {
			if (changes?.['fromDate'])
				this.changeDate('fromDate', changes['fromDate'].currentValue);

			if (changes?.['toDate'])
				this.changeDate('toDate', changes['toDate'].currentValue);

			if (changes?.['selectedBatteryVoltage'])
				this.onSelectionChanged({ filter: 'voltageFilter', value: changes['selectedBatteryVoltage'].currentValue });

			if (changes?.['selectedBatteryCapacity'])
				this.onSelectionChanged({ filter: 'capacityFilter', value: changes['selectedBatteryCapacity'].currentValue });

			if (changes?.['selectedBatteryVintage'])
				this.onSelectionChanged({ filter: 'vintageFilter', value: changes['selectedBatteryVintage'].currentValue });

			if (changes?.['selectedTruckType'])
				this.onSelectionChanged({ filter: 'truckType', value: changes['selectedTruckType'].currentValue });

			if (changes?.['selectedTruckManufacturer'])
				this.onSelectionChanged({ filter: 'truckManufacturer', value: changes['selectedTruckManufacturer'].currentValue });
		}
	}

	prepareFilters() {
		if (!this.hasVoltageFilter && !this.hasCapacityFilter)
			return;

		const batteriesCapacities = [];
		const batteriesVoltages = [];

		for (const device in this.devices) {
			const deviceData = this.devices[device];
			if (deviceData.battery_capacity && !batteriesCapacities.includes(deviceData.battery_capacity))
				batteriesCapacities.push(deviceData.battery_capacity);

			if (deviceData.battery_voltage && !batteriesVoltages.includes(deviceData.battery_voltage))
				batteriesVoltages.push(deviceData.battery_voltage);
		}

		this.batteryVoltagesOptions = this.commonService.prepareOptions(batteriesVoltages);
		this.batteryCapacitiesOptions = this.commonService.prepareOptions(batteriesCapacities);

		this.prepareParentFiltersOptions.emit({ truckTypes: structuredClone(this.truckTypes), batteryVoltagesOptions: structuredClone(this.batteryVoltagesOptions), batteryCapacitiesOptions: structuredClone(this.batteryCapacitiesOptions) });
	}

	prepareTruckTypes() {
		this.truckTypes = [{value: 'All', label: 'All'}];

		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (device.truck_type) {
				const isExist = this.truckTypes.find((type) => type.value == device.truck_type);
				if (!isExist)
					this.truckTypes.push({
						value: device.truck_type,
						label: device.truck_type,
					});
			}
		}

		this.prepareParentFiltersOptions.emit({ truckTypes: structuredClone(this.truckTypes) });
	}

	prepareTruckManufacturers() {
		const truckManufacturers = [{ value: 'All', label: 'All' }];

		for (const macAddress in this.devices) {
			const device = this.devices[macAddress];

			if (device.truck_manufacturer) {
				const isExist = truckManufacturers.find((manufacturer) => manufacturer.value == device.truck_manufacturer);
				if (!isExist)
					truckManufacturers.push({
						value: device.truck_manufacturer,
						label: device.truck_manufacturer,
					});
			}
		}

		this.prepareParentFiltersOptions.emit({ truckManufacturers: structuredClone(truckManufacturers) });
	}

	getDailyUtilizationData() {
		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));

		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		if (!this.siteId)
			return;

		this.sitesService.getDailyUtilizationData(this.siteId, fromDate, toDate, this.devicesType).subscribe((res: any) => {
			this.data = res.data || {};
			this.devices = res.devices || {};
			this.usedDevicesNumberChart.config.vAxis.title = this.chart.firstChartYAxeTitle || this.vAxisTitle;
			this.updateDateFormate();

			if (this.hasTruckTypeFilter)
				this.prepareTruckTypes();

			this.prepareTruckManufacturers();

			if (this.hasVoltageFilter || this.hasCapacityFilter)
				this.prepareFilters();

			this.prepareDeviceNumberChartData();
			this.showChart = true;
		});
	}

	getAhDailyUtilizationData() {
		if (!this.siteId)
			return;

		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));

		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		this.sitesService.getAhDailyUtilizationData(this.siteId, fromDate, toDate, this.devicesType).subscribe((res: any) => {
			this.data = res.data || {};
			this.capacities = res.capacities || {};
			this.devices = res.devices || {};
			this.usedDevicesNumberChart.config.vAxis.title = this.chart.firstChartYAxeTitle || this.vAxisTitle;
			this.updateDateFormate();

			if (this.hasTruckTypeFilter)
				this.prepareTruckTypes();

			this.prepareTruckManufacturers();

			if (this.hasVoltageFilter || this.hasCapacityFilter)
				this.prepareFilters();

			this.ahPrepareDeviceNumberChartData();
			this.showChart = true;
		});
	}

	changeDate(field, value) {
		this[field] = value;

		if (this.chartType === 'ah')
			this.getAhDailyUtilizationData();
		else
			this.getDailyUtilizationData();
	}

	updateDateFormate() {
		if (this.chart.dateFormate) {
			this.usedDevicesNumberChart.config.hAxis.format = this.chart.dateFormate;
			this.usagePercentageChart.config.hAxis.format = this.chart.dateFormate;
		}
	}

	onSelectionChanged(event) {
		switch (event.filter) {
			case 'truckType':
				this.selectedTruckType = event.value;
				break;
			case 'truckManufacturer':
				this.selectedTruckManufacturer = event.value;
				break;
			case 'voltageFilter':
				this.selectedBatteryVoltage = event.value;
				break;
			case 'capacityFilter':
				this.selectedBatteryCapacity = event.value;
				break;
			case 'vintageFilter':
				this.selectedBatteryVintage = event.value;
				break;
			default:
				break;
		}

		this.updateDateFormate();

		if (this.chartType === 'ah')
			this.ahPrepareDeviceNumberChartData();
		else
			this.prepareDeviceNumberChartData();
	}

	prepareDeviceNumberChartData() {
		const usageData = [];
		const usagePercentageData = [];

		const fromDate = moment(this.fromDate).startOf('day').unix() * 1000;
		const toDate = moment(this.toDate).endOf('day').unix() * 1000;
		const devicesCount = Object.keys(this.devices).length;

		this.updateChartTicks(fromDate, toDate);

		if (!devicesCount) {
			// Without this the early return left noData at its previous value, so the template still
			// rendered the charts with an empty data set and the chart library reported a type error.
			this.noData = true;
			return;
		}
		let hasData = false; 

		let maxValue = 0;
		for (const day in this.data) {
			let usedDevicesCount = 0;
			let unusedDevicesCount = 0;

			for (const macAddress in this.data[day]) {
				if (!this.isIncludedDevice(macAddress))
					continue;
					hasData = true; 

				if (this.data[day][macAddress])
					usedDevicesCount++;
				else
					unusedDevicesCount++;
			}

			const total = usedDevicesCount + unusedDevicesCount;
			if (total > maxValue)
				maxValue = total;

			const date = moment.unix(+day).startOf('day').toDate();

			usageData.push([
				date,
				usedDevicesCount, // used
				unusedDevicesCount, // not used
				total // total
			]);

			const percent = (usedDevicesCount * 100) / (usedDevicesCount + unusedDevicesCount);
			usagePercentageData.push([date, Math.round(+percent || 0)]);
		}

		this.usedDevicesNumberChart.data = usageData;
		this.usagePercentageChart.data = usagePercentageData;
		this.noData = !hasData;
	}

	isIncludedDevice(macAddress: string) {
		if (this.hasTruckTypeFilter && this.selectedTruckType !== 'All' && this.devices?.[macAddress]?.truck_type !== this.selectedTruckType)
			return false;

		if (this.selectedTruckManufacturer?.toLocaleLowerCase() !== 'all' && this.devices?.[macAddress]?.truck_manufacturer !== this.selectedTruckManufacturer)
			return false;

		if (this.selectedBatteryVoltage !== 'all' && this.devices?.[macAddress]?.battery_voltage !== this.selectedBatteryVoltage)
			return false;

		if (this.selectedBatteryCapacity !== 'all' && this.devices?.[macAddress]?.battery_capacity !== this.selectedBatteryCapacity)
			return false;

		if (!this.siteDashboardService.isVintageWithinFilter(this.devices?.[macAddress], this.selectedBatteryVintage))
			return false;

		return true;
	}

	ahPrepareDeviceNumberChartData() {
		const usageData = [];
		const usagePercentageData = [];

		const fromDate = moment(this.fromDate).startOf('day').unix() * 1000;
		const toDate = moment(this.toDate).endOf('day').unix() * 1000;
		const devicesCount = Object.keys(this.devices).length;

		this.updateChartTicks(fromDate, toDate);

		if (!devicesCount) {
			// Without this the early return left noData at its previous value, so the template still
			// rendered the charts with an empty data set and the chart library reported a type error.
			this.noData = true;
			return;
		}
		let hasData = false;
		let maxPercentage = 100;
		for (const day in this.data) {
			let totalDailyUsedAh  = 0;

			for (const macAddress in this.data[day]) {
				if (!this.isIncludedDevice(macAddress))
					continue;

				hasData = true;
				totalDailyUsedAh += (this.data?.[day]?.[macAddress] || 0) / 3600; // convert to hours
			}

			let total = 0;

			if (this.capacities) {
				for (const macAddress in this.capacities) {
				if (!this.isIncludedDevice(macAddress)) 
					continue;

				hasData = true; 
				total += (this.capacities[macAddress] || 0) * 0.8;
				}
			}

			let remainingAh = total - totalDailyUsedAh;
			if (remainingAh < 0)
				remainingAh = 0;

			const date = moment.unix(+day).startOf('day').toDate();
			usageData.push([
				date, // date
				Math.round(totalDailyUsedAh), // used
				Math.round(remainingAh), // not used
				Math.round(total) // total
			]);

			const percentage = total ? (totalDailyUsedAh * 100) / total : 0;
			if (percentage > maxPercentage)
				maxPercentage = percentage;

			usagePercentageData.push([date, Math.round(percentage || 0)]);
		}

		this.usedDevicesNumberChart.data = usageData;
		this.usagePercentageChart.data = usagePercentageData;
		this.noData = !hasData;
	}

	updateChartTicks(fromDate: number, toDate: number) {
	// if the days count less than 8 days update the chart steps
		if ((toDate - fromDate) < 8 * 24 * 60 * 60 * 1000) {
			this.usedDevicesNumberChart.config.hAxis.ticks = [];
			this.usagePercentageChart.config.hAxis.ticks = [];

			for (let i = fromDate; i <= toDate; i += 24 * 60 * 60 * 1000) {
			this.usedDevicesNumberChart.config.hAxis.ticks.push(new Date(i));
			this.usagePercentageChart.config.hAxis.ticks.push(new Date(i));
			}
		}
	}

	ngOnDestroy(): void {
		if (this.siteSubscription)
			this.siteSubscription.unsubscribe();

		this.showChart = false;
		this.data = {};
		this.devices = {};
		this.usedDevicesNumberChart.data = [];
		this.usagePercentageChart.data = [];
		this.usedDevicesNumberChart.config.hAxis.ticks = [];
		this.usagePercentageChart.config.hAxis.ticks = [];
	}
}
